/**
 * The device's identity: which branch this kiosk is registered to, and the
 * 90-day token that lets it scan.
 *
 * Also how this device takes check-ins (D-019): `"choice"` (the default — a
 * chooser between the scan page and "Type your name"), `"scan"` (the classic
 * single-purpose badge screen) or `"type"` (no scanner at this branch yet —
 * students type their name or number). A staff setting, kept in the same
 * record as the token so a device carries one identity, and left behind when
 * the token is cleared so re-registering keeps it.
 *
 * This is the *only* thing the app persists. The staff JWT used to mint the
 * token never reaches storage — it lives in a screen's state and is dropped as
 * soon as setup finishes (docs/decisions.md D-006).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { DeviceRegistration } from "../api/kiosk";

const STORAGE_KEY = "zebra-kiosk-device";

/**
 * The meaning of the stored `checkInMode`. Schema 1 (no `modeSchema` field,
 * the first UAT build) had two modes, and its `"scan"` was the two-option idle
 * that is `"choice"` now. Every write carries the current schema.
 */
const MODE_SCHEMA = 2;

export type CheckInMode = "choice" | "scan" | "type";

const DEFAULT_MODE: CheckInMode = "choice";

export interface DeviceState {
  kioskToken: string;
  branchId: number;
  branchName: string;
  expiresAt: string;
  checkInMode: CheckInMode;
}

/** What is actually written under STORAGE_KEY. */
type StoredRecord = (DeviceState | { checkInMode: CheckInMode }) & {
  modeSchema: typeof MODE_SCHEMA;
};

interface DeviceContextValue {
  device: DeviceState | null;
  isRegistered: boolean;
  /** Set when the device was kicked out, so /setup can explain why. */
  evictionReason: string | null;
  /**
   * The device's mode — or, while it is unregistered, the one it will be
   * registered with: the mode survives an eviction, so a type-mode kiosk whose
   * token lapsed comes back as a type-mode kiosk.
   */
  checkInMode: CheckInMode;
  /** Registers with the current `checkInMode`. */
  register: (registration: DeviceRegistration) => void;
  /** Saved at once — on the device record, or kept for the next registration. */
  setCheckInMode: (mode: CheckInMode) => void;
  /** Clear the token. `reason` is shown on the setup screen. */
  evict: (reason?: string) => void;
  clearEvictionReason: () => void;
}

const DeviceContext = createContext<DeviceContextValue | null>(null);

/**
 * The mode a stored record means, whatever build wrote it (see MODE_SCHEMA).
 * Anything unrecognised — including no mode at all, from before modes
 * existed — is the default.
 */
function parseMode(
  parsed: { checkInMode?: unknown; modeSchema?: unknown } | null,
): CheckInMode {
  const mode = parsed?.checkInMode;
  if (parsed?.modeSchema === undefined) {
    // Schema 1 — no field at all: "scan" was the two-option idle, and "type"
    // is unchanged.
    return mode === "type" ? "type" : DEFAULT_MODE;
  }
  // Schema 2 is taken as is. A schema this build does not know (a newer
  // build's, or a corrupt one) is not guessed at: the default.
  if (parsed.modeSchema !== MODE_SCHEMA) return DEFAULT_MODE;
  return mode === "choice" || mode === "scan" || mode === "type" ? mode : DEFAULT_MODE;
}

function readStoredDevice(): DeviceState | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode, or storage disabled by policy. Treat as unregistered —
    // setup will run and simply fail to persist, which is visible and honest.
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<DeviceState> & { modeSchema?: unknown };
    if (typeof parsed?.kioskToken !== "string" || !parsed.kioskToken) return null;
    if (typeof parsed?.branchId !== "number") return null;
    return {
      kioskToken: parsed.kioskToken,
      branchId: parsed.branchId,
      branchName: typeof parsed.branchName === "string" ? parsed.branchName : "",
      expiresAt: typeof parsed.expiresAt === "string" ? parsed.expiresAt : "",
      checkInMode: parseMode(parsed),
    };
  } catch {
    // Corrupt entry from an older build. Drop it rather than crash on boot.
    return null;
  }
}

/**
 * The mode on the stored record, registered or not. An evicted device leaves a
 * `{ checkInMode }` remnant under the same key — no token, so it never reads
 * as registered — so re-registering keeps the mode (D-019).
 */
function readStoredMode(): CheckInMode {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw
      ? (JSON.parse(raw) as { checkInMode?: unknown; modeSchema?: unknown })
      : null;
    return parseMode(parsed);
  } catch {
    return DEFAULT_MODE;
  }
}

/** Every write stamps the current mode schema, so it is never read as legacy. */
function writeStored(record: DeviceState | { checkInMode: CheckInMode }) {
  const stored: StoredRecord = { ...record, modeSchema: MODE_SCHEMA };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Keep it in memory anyway: the kiosk works for this session, and the
    // next reload sends staff back through setup. Better than refusing.
  }
}

export function DeviceProvider({ children }: { children: ReactNode }) {
  const [device, setDevice] = useState<DeviceState | null>(readStoredDevice);
  // Only consulted while `device` is null; a registered device's own record wins.
  const [unregisteredMode, setUnregisteredMode] = useState<CheckInMode>(readStoredMode);
  const [evictionReason, setEvictionReason] = useState<string | null>(null);
  const checkInMode = device?.checkInMode ?? unregisteredMode;

  const register = useCallback(
    (registration: DeviceRegistration) => {
      const next: DeviceState = {
        kioskToken: registration.kioskToken,
        branchId: registration.branch_id,
        branchName: registration.branch_name ?? "",
        expiresAt: registration.expires_at ?? "",
        // Re-registering keeps how the device takes check-ins.
        checkInMode,
      };
      writeStored(next);
      setEvictionReason(null);
      setDevice(next);
    },
    [checkInMode],
  );

  const setCheckInMode = useCallback(
    (mode: CheckInMode) => {
      if (mode === checkInMode) return;
      if (device) {
        const next: DeviceState = { ...device, checkInMode: mode };
        writeStored(next);
        setDevice(next);
      } else {
        writeStored({ checkInMode: mode });
        setUnregisteredMode(mode);
      }
    },
    [checkInMode, device],
  );

  const evict = useCallback(
    (reason?: string) => {
      // The token goes; the mode stays (see readStoredMode). A token lapsing
      // after 90 days is the normal way here, and must not turn a branch with
      // no scanner back into a scanning kiosk.
      writeStored({ checkInMode });
      setUnregisteredMode(checkInMode);
      setDevice(null);
      setEvictionReason(reason ?? null);
    },
    [checkInMode],
  );

  const clearEvictionReason = useCallback(() => setEvictionReason(null), []);

  // A branch may run two kiosks in two tabs on one machine. If one
  // re-registers or is evicted, the other should follow rather than keep
  // scanning with a token that is no longer the device's identity.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== null && event.key !== STORAGE_KEY) return;
      setDevice(readStoredDevice());
      setUnregisteredMode(readStoredMode());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo<DeviceContextValue>(
    () => ({
      device,
      isRegistered: device !== null,
      checkInMode,
      evictionReason,
      register,
      setCheckInMode,
      evict,
      clearEvictionReason,
    }),
    [
      device,
      checkInMode,
      evictionReason,
      register,
      setCheckInMode,
      evict,
      clearEvictionReason,
    ],
  );

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDevice(): DeviceContextValue {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error("useDevice must be used inside <DeviceProvider>");
  }
  return context;
}
